import { WorkflowType } from '@constants/dispositions';
import { getLocalStorageItem, setLocalStorageItem } from '@utils/localStorage';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useStore } from '@/store';

import { useIs100msUser } from './useIs100msUser';

type DynamicFormData = Record<string, any>;

interface UseAutoFillReturn {
  isAutoFillEnabled: boolean;
  hasSavedData: boolean;
  autoFill: () => DynamicFormData | null;
  saveFormData: (data: DynamicFormData) => void;
}

/**
 * Custom hook to manage auto-fill functionality for forms
 *
 * Features:
 * - Checks if auto-fill is enabled for the current workflow type
 * - Manages localStorage for saving/loading form data
 * - Provides methods to auto-fill and save form data
 *
 * @returns Auto-fill state and methods
 */
export const useAutoFill = (): UseAutoFillReturn => {
  const [hasSavedData, setHasSavedData] = useState(false);
  const is100msUser = useIs100msUser();

  const { getSelectedWorkflow } = useStore();
  const selectedWorkflow = getSelectedWorkflow();

  // Auto-fill is only enabled for 100ms users in recall_scheduler workflow type
  const isAutoFillEnabled =
    is100msUser && selectedWorkflow?.workflow_type === WorkflowType.RECALL_SCHEDULER;

  // Local storage key for this workflow
  const localStorageKey = selectedWorkflow?.id ? `createCase_${selectedWorkflow.id}` : null;

  // Check for saved data on mount and when workflow changes
  useEffect(() => {
    if (localStorageKey && isAutoFillEnabled) {
      const savedData = getLocalStorageItem<DynamicFormData>(localStorageKey);
      setHasSavedData(!!savedData);
    } else {
      setHasSavedData(false);
    }
  }, [localStorageKey, isAutoFillEnabled]);

  // Auto-fill form with saved data
  const autoFill = (): DynamicFormData | null => {
    if (!localStorageKey) {
      toast.error('Unable to load saved data');
      return null;
    }

    const savedData = getLocalStorageItem<DynamicFormData>(localStorageKey);
    if (savedData) {
      toast.success('Form filled with previously saved data');
      return savedData;
    } else {
      toast.error('No saved data found');
      setHasSavedData(false);
      return null;
    }
  };

  // Save form data to localStorage
  const saveFormData = (data: DynamicFormData): void => {
    if (!localStorageKey || !isAutoFillEnabled) {
      return;
    }

    setLocalStorageItem(localStorageKey, data);
    setHasSavedData(true);
  };

  return {
    isAutoFillEnabled,
    hasSavedData,
    autoFill,
    saveFormData,
  };
};
